import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import { PrivateRoutes } from '../routes/private.routes';

// === Interfaces ===

export interface ITelaAcao {
  id: number;
  acao: string;
  permissao_id: number | null;
}

export interface ITela {
  id: number;
  codigo: string;
  nome: string;
  path: string;
  parent_codigo: string | null;
  ativo: boolean;
  acoes: ITelaAcao[];
}

export interface ITelaSyncRequest {
  telas: {
    codigo: string;
    nome: string;
    path: string;
    parent_codigo?: string;
    acoes: string[];
  }[];
}

export interface ITelaSyncResponse {
  criadas: number;
  atualizadas: number;
  desativadas: number;
}

export interface ITelaPermissaoGrupo {
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  tela_path: string;
  acoes: string[];
}

export interface IGrupoTelasRequest {
  permissoes: {
    tela_id: number;
    acoes: string[];
  }[];
}

interface ITelaFilter {
  telaId: string;
}

interface IGrupoIdFilter {
  grupoId: string;
}

// === Telas do sistema (rotas do front) ===

/**
 * Gera um código padronizado em kebab-case a partir do path
 * Exemplo: /modulesHome/integration/sequenceBreak -> moduleshome-integration-sequencebreak
 */
export const gerarCodigoFromPath = (path: string): string => {
  return path
    .replace(/^\//, '') // Remove barra inicial
    .replace(/\//g, '-') // Substitui barras por hífens
    .replace(/:[\w]+/g, 'param') // Substitui parâmetros (:id) por 'param'
    .toLowerCase(); // Tudo minúsculo
};

/**
 * Obtém o código do parent de uma rota
 */
const getParentCodigo = (route: (typeof PrivateRoutes)[0]): string | undefined => {
  if (!route.isChildrenMenu || !route.parentMenu) return undefined;

  const parent = PrivateRoutes.find(r => r.name === route.parentMenu && r.isParentMenu);
  if (!parent) return undefined;

  // Usa o codigo definido ou gera a partir do path
  return parent.codigo || gerarCodigoFromPath(parent.path);
};

/** Telas e ações declaradas no código do front: a base da sincronização e da
 * comparação "o que tem de novo" da Central de Permissões. */
export function montarTelasDoSistema(): ITelaSyncRequest['telas'] {
  // Monta a lista de telas a partir das rotas do frontend
  const telas = PrivateRoutes.filter(route => route.path !== '/' && !route.semTela).map(route => {
    // Determina as ações disponíveis para cada tela
    const acoes: string[] = ['view']; // Todas as telas têm view

    if (route.componentCreate) acoes.push('create');
    if (route.componentEdit) acoes.push('edit');
    if (route.componentShow) acoes.push('show');

    // Usa o codigo definido na rota ou gera a partir do path
    const codigo = route.codigo || gerarCodigoFromPath(route.path);

    return {
      codigo,
      nome: route.name,
      path: route.path,
      parent_codigo: getParentCodigo(route),
      acoes,
    };
  });

  // Telas extras com ações customizadas (não derivadas de rotas)
  // Toda acao que o back confere tem de estar aqui: o "sincronizar" desativa a que falta
  // (pular_coluna, avancar_card e criar_card vem da migration f7e8d9c0b1a2).
  telas.push({
    codigo: 'kanban_eventos',
    nome: 'Kanban de Eventos',
    path: '/kanban/eventos',
    parent_codigo: 'kanban',
    acoes: [
      'retroceder_card',
      'editar_coluna',
      'excluir_coluna',
      'mover_coluna',
      'editar_card',
      'pular_coluna',
      'avancar_card',
      'criar_card',
    ],
  });
  // Abas e aviso de Eventos (back: app/services/eventos/acoes.py). Path fora de `/eventos/`: o
  // `hasPermission` herda entre pai e sub-telas, e quem so' recebe o aviso ganharia o menu Eventos.
  // Sem esta entrada o "sincronizar" desativa a tela e ninguem fora do Admin ve as abas nem recebe aviso.
  telas.push({
    codigo: 'eventos_acoes',
    nome: 'Eventos: abas e avisos',
    path: '/eventos-acoes',
    parent_codigo: 'eventos',
    acoes: ['ver_painel_executivo', 'ver_financeiro', 'receber_aviso_assinatura'],
  });

  // WhatsApp — substitui acoes padrao (view) por acoes especificas de cada modulo
  const waOverrides: Record<string, string[]> = {
    'whatsapp-chat': [
      'assumir_fila',
      'assumir_departamento',
      'assumir_todos',
      'transferir_proprio',
      'transferir_departamento',
      'transferir_todos',
      'responder',
      'nota_interna',
      'finalizar',
      'abrir_chamado',
    ],
    'whatsapp-kanban': [
      'visualizar',
      'criar',
      'editar_proprio',
      'editar_todos',
      'excluir',
      'mover',
    ],
    'whatsapp-contatos': ['visualizar', 'criar', 'editar', 'excluir', 'iniciar_conversa'],
    'whatsapp-dashboard': ['visualizar'],
    'whatsapp-relatorios': ['visualizar'],
    'whatsapp-conexao': ['visualizar', 'editar'],
    'whatsapp-historico': ['visualizar'],
    'whatsapp-ai-insights': ['visualizar'],
  };

  Object.entries(waOverrides).forEach(([codigo, acoes]) => {
    const tela = telas.find(t => t.codigo === codigo);
    if (tela) tela.acoes = acoes;
  });

  // Ações da Emissão de NFS-e que o backend confere (seed 0298). Sem elas aqui a
  // sincronização antiga apagava emitir/cancelar/certificado/configurar.
  const nfse = telas.find(t => t.codigo === 'nfse-emissao');
  if (nfse)
    nfse.acoes = Array.from(
      new Set([...nfse.acoes, 'emitir', 'cancelar', 'certificado', 'configurar']),
    );

  return telas;
}

// === Hook ===

export const useTelas = () => {
  // Listar todas as telas
  const getTelas = async (key: QueryFunctionContext): Promise<IData<ITela[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ITela[]>>('/api/v1/telas/', {
      params: filters,
    });
    return res.data;
  };

  // Obter uma tela por ID
  const getTela = async (key: QueryFunctionContext): Promise<IDataNoPagination<ITela>> => {
    const { telaId } = key.queryKey[1] as ITelaFilter;
    const res = await api.get<IDataNoPagination<ITela>>(`/api/v1/telas/${telaId}/`);
    return res.data;
  };

  // Sincronizar telas do frontend com o backend
  const syncTelas = async (): Promise<IDataNoPagination<ITelaSyncResponse>> => {
    const telas = montarTelasDoSistema();
    const res = await api.post<IDataNoPagination<ITelaSyncResponse>>('/api/v1/telas/sincronizar/', {
      telas,
    });
    return res.data;
  };

  // Obter permissões de tela de um grupo
  const getGrupoTelas = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<ITelaPermissaoGrupo[]>> => {
    const { grupoId } = key.queryKey[1] as IGrupoIdFilter;
    const res = await api.get<IDataNoPagination<ITelaPermissaoGrupo[]>>(
      `/api/v1/grupos/${grupoId}/telas/`,
    );
    return res.data;
  };

  // Definir permissões de tela de um grupo
  const setGrupoTelas = async (data: {
    grupoId: number;
    permissoes: { tela_id: number; acoes: string[] }[];
  }): Promise<IDataNoPagination<ITelaPermissaoGrupo[]>> => {
    const res = await api.put<IDataNoPagination<ITelaPermissaoGrupo[]>>(
      `/api/v1/grupos/${data.grupoId}/telas/`,
      { permissoes: data.permissoes },
    );
    return res.data;
  };

  return {
    getTelas,
    getTela,
    syncTelas,
    getGrupoTelas,
    setGrupoTelas,
  };
};
